/**
 * @file CoreLifecycle - Shared base for components with managed lifecycle.
 *
 * Three-phase lifecycle on top of `CoreClass`:
 *
 * 1. **Constructor** (sync) — create object graph, adopt children.
 * 2. **configure()** (async) — resolve secrets, validate config, wire deps.
 * 3. **start()** (async) — begin work (port-bind for servers, connect for
 *    clients).
 * 4. **stop()** (async) — cleanup.
 *
 * State machine (strict):
 * `created → configuring → configured → starting → running → stopping → stopped`
 *
 * Adopted lifecycle-participating children are configured sequentially
 * (order matters for deps), started in parallel (independent post-
 * configure), and stopped in reverse adoption order.
 *
 * `CoreLifecycle` is the shared base for `CoreService` (publishes
 * routes/subscriptions) and `CoreClient` (holds a typed connection to a
 * remote server). Subclasses override the protected hooks `onConfigure`,
 * `onStart`, `onStop` — the public lifecycle methods own state transitions,
 * telemetry, and the child-walk.
 *
 * @module
 */

import { z } from 'zod'
import { CoreClass } from './core_class.ts'
import { CoreError } from './core_error.ts'
import { metricName } from './core_telemetry.ts'
import { runInSpan } from './span.ts'

/**
 * Health status returned by the `health` getter.
 *
 * @property healthy - Whether the entity is considered healthy.
 * @property timestamp - ISO 8601 check timestamp.
 * @property error - Error message if unhealthy.
 * @property details - Subclass-supplied detail bag.
 */
export const HealthSchema = z.object({
  healthy: z.boolean(),
  timestamp: z.string().datetime(),
  error: z.string().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
})

/** @see {@link HealthSchema}. */
export type Health = z.infer<typeof HealthSchema>

/**
 * Lifecycle state. Valid transitions:
 *
 * - `created → configuring → configured → starting → running → stopping → stopped`
 * - `configuring → stopped` and `starting → stopped` on phase failure
 */
export type LifecycleState =
  | 'created'
  | 'configuring'
  | 'configured'
  | 'starting'
  | 'running'
  | 'stopping'
  | 'stopped'

/**
 * Per-state numeric code for the `lifecycle.state_code` gauge. Picked
 * monotonic from youngest to oldest so a single threshold rule can split
 * "still starting" / "live" / "winding down". The exact integers are
 * arbitrary; only stability across rebuilds matters.
 */
const LIFECYCLE_STATE_CODE: Record<LifecycleState, number> = {
  created: 0,
  configuring: 1,
  configured: 2,
  starting: 3,
  running: 4,
  stopping: 5,
  stopped: 6,
}

// Metric name brands hoisted to module scope so each setState / wrapPhase
// call skips the `metricName()` type-erasure cast.
const LIFECYCLE_TRANSITION_TOTAL = metricName('lifecycle.transition_total')
const LIFECYCLE_STATE_CODE_GAUGE = metricName('lifecycle.state_code')
const LIFECYCLE_HEALTH_GAUGE = metricName('health')
const LIFECYCLE_CONFIGURE_DURATION = metricName(
  'lifecycle.configure.duration_ms',
)
const LIFECYCLE_START_DURATION = metricName('lifecycle.start.duration_ms')
const LIFECYCLE_STOP_DURATION = metricName('lifecycle.stop.duration_ms')

const PHASE_DURATION_METRIC: Record<
  'configure' | 'start' | 'stop',
  ReturnType<typeof metricName>
> = {
  configure: LIFECYCLE_CONFIGURE_DURATION,
  start: LIFECYCLE_START_DURATION,
  stop: LIFECYCLE_STOP_DURATION,
}

/**
 * Abstract base for entities with a managed lifecycle. Once stopped, an
 * instance cannot be restarted — construct a new one. Phase failures
 * transition to `stopped` and throw `CoreError`. `stop()` attempts every
 * child even if some fail and chains the errors into a single `CoreError`.
 */
export abstract class CoreLifecycle<
  TProps extends { id?: string } = { id?: string },
  TEvents extends Record<string, readonly unknown[]> = {},
> extends CoreClass<TProps, TEvents> {
  private _state: LifecycleState = 'created'
  private _startSignal: AbortSignal | undefined = undefined

  get state(): LifecycleState {
    return this._state
  }

  get running(): boolean {
    return this._state === 'running'
  }

  /**
   * Adopted children that participate in the lifecycle (i.e. are themselves
   * `CoreLifecycle` instances). The `instanceof` filter is defensive against
   * pure-`CoreClass` adopted children (observers, span impls) that don't
   * have configure/start/stop.
   */
  protected get lifecycleChildren(): CoreLifecycle[] {
    return this.children.filter(
      (child): child is CoreLifecycle => child instanceof CoreLifecycle,
    )
  }

  /**
   * Current health status, frozen. Subclasses customise via `isHealthy()`
   * and `getHealthDetails()`.
   */
  get health(): Health {
    const details = this.getHealthDetails()
    const health: Health = {
      healthy: this.isHealthy(),
      timestamp: new Date().toISOString(),
    }
    if (Object.keys(details).length > 0) {
      return Object.freeze({ ...health, details })
    }
    return Object.freeze(health)
  }

  /**
   * `AbortSignal` from the most recent `start()` call, or `undefined`.
   * Available to `onStart` implementations that need cancelable I/O.
   */
  protected get startSignal(): AbortSignal | undefined {
    return this._startSignal
  }

  /**
   * Configure this entity and all lifecycle children. Calls `onConfigure()`
   * then configures children sequentially (in adoption order — dependencies
   * matter). Emits `lifecycle.transition_total{from,to}` on each transition
   * and a `lifecycle.configure.duration_ms` histogram via the wrapping span.
   *
   * @throws CoreError(`INVALID_STATE`) if not in `'created'`;
   *   CoreError(`CONFIGURE_FAILED`) on failure.
   */
  async configure(): Promise<void> {
    this.guardState('created')
    this.setState('configuring')
    this.debug('configure')

    return this.wrapPhase('configure', async () => {
      try {
        await this.onConfigure()

        // Skip children that are not in `created` — a subclass (or
        // its caller) may have pre-configured / pre-started them
        // out-of-band (e.g. TestHarness fixtures whose `rootToken` is
        // mint-on-init and must run before downstream children can be
        // constructed). Those children are "already configured" from
        // the framework's perspective and don't need a second pass.
        for (const child of this.lifecycleChildren) {
          if (child.state !== 'created') continue
          await child.configure()
        }

        this.setState('configured')
        this.info('configure')
      } catch (err) {
        this.setState('stopped')
        const error = CoreError.from(err, 'CONFIGURE_FAILED')
        this.error('configure', error)
        throw error
      }
    })
  }

  /**
   * Start this entity and all lifecycle children. Calls `onStart()` then
   * starts children in parallel (independent after configure). On failure,
   * awaits stragglers and stops anything that did start (cleanup never
   * leaks).
   *
   * @param opts.signal - Optional `AbortSignal` propagated to children and
   *   (via `startSignal`) available to subclasses' `onStart` for cancelable
   *   I/O. The base does not itself watch the signal — subclasses that
   *   block on async ops (TestDaemon's `initialize` / `waitForReady` /
   *   spawn-and-wait) must thread it through so a caller's deadline can
   *   preempt in-flight work. Test fixtures pass mocha's hook timeout via
   *   `AbortSignal.timeout(this.timeout())` to avoid leaking subprocesses
   *   past the hook deadline.
   * @throws CoreError(`INVALID_STATE`) if not in `'configured'`;
   *   CoreError(`START_FAILED`) on failure.
   */
  async start(opts: { signal?: AbortSignal } = {}): Promise<void> {
    this.guardState('configured')
    this.setState('starting')
    this._startSignal = opts.signal
    this.debug('start')

    return this.wrapPhase('start', async () => {
      // Capture the children snapshot BEFORE `onStart` runs so that
      // subclasses whose `onStart` adopts and manually starts
      // additional children (e.g. Email's lazy SmtpProvider adoption
      // after Bao supplies the credentials) don't get those children
      // double-started by the framework's child walk below. The
      // contract: the framework starts the children that existed at
      // `start()` entry; anything `onStart` adopts is the subclass's
      // own to manage.
      const children = this.lifecycleChildren
      try {
        await this.onStart()
        await this.startChildren(children, { signal: opts.signal })
        this.setState('running')
        this.info('start')
      } catch (err) {
        this.setState('stopped')
        const error = CoreError.from(err, 'START_FAILED')
        this.error('start', error)
        throw error
      }
    })
  }

  /**
   * Start the lifecycle-participating children captured at `start()`
   * entry. Default behaviour: fan parallel via `Promise.all`.
   * Cleanup-on-failure waits for in-flight stragglers via
   * `Promise.allSettled` and stops any children that did reach
   * `running`, so a fast-fail rejection from one sibling cannot leak
   * the successfully-spawned subprocess of another.
   *
   * Subclasses override this to express richer start topologies —
   * e.g. {@link TestHarness} with its layered services. The
   * `children` argument is the framework-captured pre-onStart
   * snapshot; subclasses that maintain their own topology metadata
   * (TestHarness's `layers`) typically ignore it and walk their
   * own structure instead.
   */
  protected async startChildren(
    children: CoreLifecycle[],
    opts: { signal?: AbortSignal },
  ): Promise<void> {
    // Skip children that are already running. Symmetric with the
    // configure-walk skip — a pre-running child (e.g. TestHarness's
    // out-of-band `bao`) is "already done" from the framework's
    // perspective.
    const toStart = children.filter(c => c.state !== 'running')
    const promises = toStart.map(child => child.start({ signal: opts.signal }))
    try {
      await Promise.all(promises)
    } catch (err) {
      await Promise.allSettled(promises)
      // Only stop children WE attempted to start — pre-running
      // children stay running; their lifecycle is owned by the caller
      // that pre-started them.
      await Promise.allSettled(
        toStart
          .filter(child => child.state === 'running')
          .map(child => child.stop()),
      )
      throw err
    }
  }

  /**
   * Stop this entity and all lifecycle children in reverse adoption order.
   * Idempotent in both directions: already-stopped exits cleanly;
   * never-started exits cleanly. Entry states are `running`, `configured`,
   * `starting` — the widening from `running`-only is so a SIGTERM
   * mid-startup can tear down whatever children did start
   * (`Daemon.shutdown`'s mid-startup abort path depends on this). Uses
   * `Promise.allSettled` to attempt every child even if some fail; partial
   * failures throw a chained `CoreError`.
   *
   * @throws CoreError(`STOP_FAILED`) on failure.
   */
  async stop(): Promise<void> {
    if (
      this._state === 'stopped' ||
      this._state === 'stopping' ||
      this._state === 'created' ||
      this._state === 'configuring'
    ) {
      return
    }

    this.setState('stopping')
    this.debug('stop')

    return this.wrapPhase('stop', async () => {
      const errors: CoreError[] = []

      try {
        const childErrors = await this.stopChildren()
        errors.push(...childErrors)
        await this.onStop()
      } catch (err) {
        errors.push(CoreError.from(err, 'STOP_FAILED'))
      } finally {
        this.setState('stopped')
      }

      if (errors.length > 0) {
        const error = this.chainErrors(errors, 'STOP_FAILED', 'Stop failed')
        this.error('stop', error)
        throw error
      }

      this.info('stop')
    })
  }

  /**
   * Stop the lifecycle-participating children, **sequentially in
   * reverse adoption order** — the mirror of the sequential
   * configure/start walk. A child adopted on top of a dependency (a
   * Postgres/Redis client adopted after the OpenBao client it revokes
   * its lease through) is therefore fully down before that dependency,
   * so teardown never races a still-needed dep offline (#919). Per-child
   * failures are collected as `CHILD_STOP_FAILED` and the walk continues
   * — one wedged child must not strand its siblings. Subclasses override
   * to reverse-walk richer topologies — e.g. {@link TestHarness}'s
   * layered services tear down layer-by-layer in reverse.
   */
  protected async stopChildren(): Promise<CoreError[]> {
    const errors: CoreError[] = []
    const children = this.lifecycleChildren.slice().reverse()
    for (const child of children) {
      try {
        await child.stop()
      } catch (err) {
        errors.push(
          CoreError.from(err, 'CHILD_STOP_FAILED', { childId: child.id }),
        )
      }
    }
    return errors
  }

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      state: this._state,
    }
  }

  // ── Lifecycle hooks (override in subclasses) ──────────────────────────────

  /**
   * Configuration logic; called before lifecycle children are configured.
   * Use to resolve secrets, validate config, inject deps into children.
   */
  protected async onConfigure(): Promise<void> {}

  /**
   * Start logic; called before lifecycle children are started. Subclasses
   * (server-shaped: bind a port; client-shaped: open a connection) override.
   */
  protected async onStart(): Promise<void> {}

  /**
   * Cleanup logic; called after lifecycle children are stopped. Subclasses
   * (server-shaped: close the port; client-shaped: close the connection)
   * override.
   */
  protected async onStop(): Promise<void> {}

  /**
   * Helper for subclasses publishing a typed reconfigure API. Asserts
   * the entity is in `configured` or `running` state, then re-runs
   * `onConfigure`. Failure surfaces as `RECONFIGURE_FAILED` and leaves
   * state unchanged. Subclasses (e.g. `HttpClientTransport.reconfigure`)
   * apply new field state synchronously first, then `await
   * this.runReconfigure()` to re-run any async setup.
   *
   * Named distinctly from `reconfigure` so subclasses can declare a
   * public `reconfigure(props)` with their own typed signature without
   * an override-arity collision.
   *
   * @throws CoreError(`INVALID_STATE`) if not in `configured` /
   *   `running`; CoreError(`RECONFIGURE_FAILED`) on hook failure.
   */
  protected async runReconfigure(): Promise<void> {
    this.guardState(['configured', 'running'])
    try {
      await this.onConfigure()
    } catch (err) {
      throw CoreError.from(err, 'RECONFIGURE_FAILED')
    }
  }

  /** Throw `CoreError(INVALID_STATE)` if state doesn't match expected. */
  protected guardState(expected: LifecycleState | LifecycleState[]): void {
    const allowed = Array.isArray(expected) ? expected : [expected]
    if (!allowed.includes(this._state)) {
      throw new CoreError({
        code: 'INVALID_STATE',
        message: `Expected state ${allowed.join(' or ')}, got ${this._state}`,
        metadata: { currentState: this._state, expectedStates: allowed },
      })
    }
  }

  /** Default: healthy iff `running`. Override in subclasses. */
  protected isHealthy(): boolean {
    return this._state === 'running'
  }

  /** Default: empty bag. Override to add health detail fields. */
  protected getHealthDetails(): Record<string, unknown> {
    return {}
  }

  /**
   * Transition to `next`, emitting:
   *   • `lifecycle.transition_total{from,to}` counter +1
   *   • `lifecycle.state_code` gauge (numeric per-state code)
   *   • `health` gauge (1 / 0 driven by `isHealthy()`)
   *
   * Centralising state writes here means every transition shows up in
   * Prometheus and there's exactly one site to update for a new lifecycle
   * state. The duration histogram lives on {@link wrapPhase}'s span `onEnd`
   * so a single phase doesn't emit two histogram samples.
   */
  private setState(next: LifecycleState): void {
    const from = this._state
    this._state = next
    this.counter(LIFECYCLE_TRANSITION_TOTAL, { from, to: next })
    this.gauge(LIFECYCLE_STATE_CODE_GAUGE, LIFECYCLE_STATE_CODE[next])
    this.gauge(LIFECYCLE_HEALTH_GAUGE, this.isHealthy() ? 1 : 0)
  }

  /**
   * Wrap a lifecycle phase in a span so daemon startup forms a trace tree
   * (auto-correlating every child's log line via `AsyncLocalStorage` on the
   * backend) and feed the phase's duration histogram from the span's `onEnd`
   * rather than a parallel `timer()` — keeping the two recordings in
   * lock-step.
   *
   * `shared/span.ts`'s `runInSpan` is the stack-based fallback; the backend
   * re-export under `@labcraft/core/backend` shadows it with an
   * `AsyncLocalStorage`-backed implementation. Using the shared version
   * keeps this file Node-agnostic — frontend subclasses get tracing too
   * (best-effort, sequential-only).
   */
  private async wrapPhase<T>(
    phase: 'configure' | 'start' | 'stop',
    fn: () => Promise<T>,
  ): Promise<T> {
    const histogramName = PHASE_DURATION_METRIC[phase]
    return runInSpan(
      `${this.fullId}.${phase}`,
      async () => fn(),
      span => {
        // Feed the duration histogram off the span's onEnd rather than a
        // parallel `timer()` so the two recordings can't drift (no
        // double-emit). `status` splits "all configure calls" vs
        // "configure failures" without a join.
        this.histogram(histogramName, span.duration, { status: span.status })
      },
    )
  }

  /** Chain multiple errors into a single CoreError with nested causes. */
  private chainErrors(
    errors: CoreError[],
    code: string,
    message: string,
  ): CoreError {
    if (errors.length === 1) {
      return errors[0]!
    }

    let chained: CoreError = errors[0]!
    for (let i = 1; i < errors.length; i++) {
      chained = new CoreError({
        code,
        message: `${message}: ${errors[i]!.message}`,
        cause: chained,
        metadata: { ...errors[i]!.metadata },
      })
    }

    return new CoreError({
      code,
      message: `${message}: ${errors.length} errors occurred`,
      cause: chained,
      metadata: { errorCount: errors.length },
    })
  }
}
